/**
 * publishSite — main-thread publish orchestrator (T4.10, local-first).
 *
 * Gathers the editor's live state (xyflow nodes/edges from the Yjs doc,
 * grids from the global gridStore), serializes it to the on-disk source
 * format (serializeSiteDoc), then hands it to the OPFS publish worker
 * which commits + pushes via wasm-git through the same-origin BFF proxy.
 *
 * Replaces the old canned POST /api/sites/:id/publish.
 */
import type * as Y from "yjs";
import { useGridStore, type GridState } from "@fredericrous/grid";
import type { SiteThemeOrLegacy } from "@website-builder/schemas";
import {
  syncBlobStores,
  assetKey,
  ASSET_PREFIX,
} from "@website-builder/blob-store";
import { OpfsBlobStore } from "@website-builder/blob-store/opfs";

import { buildSiteFiles, serializeForSite } from "./buildSiteFiles.js";
import { withTimeout } from "./withTimeout.js";
import {
  classifyPublishError,
  publishErrorFromThrown,
  type PublishError,
} from "./publishError.js";
import { collectImageAssets } from "./siteAssets.js";
import { RemoteAssetBlobStore } from "./remoteAssetStore.js";
import { getSyncedShas, markSynced } from "@website-builder/editor";
import type { ActiveDesignSystem } from "../designSystem/designSystemFlows.js";
import { customRenderFor } from "../designSystem/prerender.js";

export interface PublishInput {
  readonly ydoc: Y.Doc;
  readonly siteId: string;
  readonly siteName: string;
  readonly theme?: SiteThemeOrLegacy;
  /**
   * OPFS site id the locally-stored images live under. Defaults to `siteId`.
   * Differs only when a local draft is published online for the first time:
   * the repo/remote target is the freshly-minted cloud id, but the images
   * still sit in OPFS under the draft's local id.
   */
  readonly assetSiteId?: string;
  /** The site's active design system (absent or Duro: render in Duro). */
  readonly designSystem?: ActiveDesignSystem;
}

/**
 * A discriminated union, not `{ok, error?}`: the old shape admitted
 * `{ok: true, error: "boom"}` and forced every caller to `?? "publish failed"`.
 * `failure` carries a TAG so the UI can pick the right next step (sign in vs
 * refresh vs upgrade) instead of printing raw git stderr.
 */
export type PublishOutcome =
  | {
      readonly ok: true;
      /** serializer warnings (omitted widgets, missing grids). */
      readonly warnings: ReadonlyArray<string>;
      /** git stdout+stderr, for diagnostics. */
      readonly log: ReadonlyArray<string>;
    }
  | {
      readonly ok: false;
      readonly failure: PublishError;
      readonly warnings: ReadonlyArray<string>;
      readonly log: ReadonlyArray<string>;
    };

interface WorkerResult {
  ok: boolean;
  error?: string;
  stdout: string[];
  stderr: string[];
}

function runWorker(worker: Worker, req: unknown): Promise<WorkerResult> {
  const rpc = new Promise<WorkerResult>((resolve, reject) => {
    worker.onmessage = (e: MessageEvent) => {
      const data = e.data as { ready?: boolean } & WorkerResult;
      if (data?.ready) {
        worker.postMessage(req);
        return;
      }
      resolve(data);
    };
    worker.onerror = (e) =>
      reject(new Error(e.message || "publish worker error"));
    worker.onmessageerror = () =>
      reject(new Error("publish worker sent an undeserializable message"));
  });
  // A wedged worker would otherwise leave `publishing` true forever, with the
  // Publish button disabled and no error shown.
  return withTimeout(rpc, "publish");
}

/** Max asset uploads in flight at once during a publish. */
const ASSET_UPLOAD_CONCURRENCY = 4;

/**
 * Deferred, local-first asset sync, expressed as a content-addressed copy
 * between BlobStores: the draft's OPFS bucket → the site's cloud asset bucket
 * (RemoteAssetBlobStore, over the BFF's POST /api/sites/:id/assets). Scoped to
 * the images the site actually references (not every blob in OPFS) and deduped
 * via the local synced manifest (the remote's `has`). Returns warnings for any
 * asset that couldn't be synced — non-fatal (the page still publishes; the
 * image resolves once a later publish lands the bytes).
 */
async function syncSiteAssets(
  siteId: string,
  assetSiteId: string,
  grids: Readonly<Record<string, GridState>>,
): Promise<string[]> {
  const referenced = collectImageAssets(grids);
  if (referenced.length === 0) return [];

  const synced = await getSyncedShas(assetSiteId);
  const contentTypeBySha = new Map(referenced.map((a) => [a.sha256, a.mime]));
  const opfs = new OpfsBlobStore(["sites", assetSiteId]);
  const remote = new RemoteAssetBlobStore({
    siteId,
    synced,
    contentTypeForKey: (key) =>
      contentTypeBySha.get(key.slice(ASSET_PREFIX.length)),
  });

  const result = await syncBlobStores(opfs, remote, {
    keys: referenced.map((a) => assetKey(a.sha256)),
    concurrency: ASSET_UPLOAD_CONCURRENCY,
  });

  const uploadedShas = result.copied.map((k) => k.slice(ASSET_PREFIX.length));
  if (uploadedShas.length > 0) await markSynced(assetSiteId, uploadedShas);
  return result.failed.map(
    (f) => `asset ${f.key.slice(ASSET_PREFIX.length).slice(0, 8)}: ${f.error}`,
  );
}

export async function publishSite(
  input: PublishInput,
): Promise<PublishOutcome> {
  const grids = useGridStore.getState().grids;
  const siteInput = {
    ydoc: input.ydoc,
    siteId: input.siteId,
    siteName: input.siteName,
    ...(input.theme ? { theme: input.theme } : {}),
  };
  const render = await customRenderFor(
    input.designSystem,
    () => serializeForSite(siteInput).doc,
  );
  if (!render.ok)
    return {
      ok: false,
      failure: { _tag: "InvalidSite", detail: `• ${render.error}` },
      warnings: [],
      log: [],
    };
  const { files, warnings, errors } = buildSiteFiles({
    ...siteInput,
    ...(render.custom ? { customDesignSystem: render.custom } : {}),
  });
  // Nothing leaves the device for a site that would publish wrong (a form in
  // a form, a submit button with no form…): no asset upload, no push.
  if (errors.length > 0)
    return {
      ok: false,
      failure: {
        _tag: "InvalidSite",
        detail: errors.map((e) => `• ${e.message}`).join("\n"),
      },
      warnings,
      log: [],
    };

  // Local-first asset sync: upload OPFS-stored images not yet on the backend
  // before pushing content, so the published page can resolve them. Images
  // live under the draft's local id when a local draft is going online for
  // the first time (assetSiteId), else under the site id.
  const assetWarnings = await syncSiteAssets(
    input.siteId,
    input.assetSiteId ?? input.siteId,
    grids,
  );

  const remoteUrl = `${location.origin}/api/git/${input.siteId}.git`;
  const worker = new Worker(
    new URL("./opfsPublishWorker.ts", import.meta.url),
    { type: "module" },
  );
  const allWarnings = [...warnings, ...assetWarnings];
  try {
    const result = await runWorker(worker, {
      siteId: input.siteId,
      remoteUrl,
      files,
      commitMessage: `Publish ${new Date().toISOString()}`,
    });
    const log = [...(result.stdout ?? []), ...(result.stderr ?? [])];
    return result.ok
      ? { ok: true, warnings: allWarnings, log }
      : {
          ok: false,
          // Classified HERE, where the worker's log is still in hand — the
          // HTTP status that distinguishes "signed out" from "out of quota"
          // only ever appears in git's stderr.
          failure: classifyPublishError({ error: result.error, log }),
          warnings: allWarnings,
          log,
        };
  } catch (e) {
    // A rejected RPC (worker crash, timeout) used to escape publishSite
    // entirely and leave the caller's `publishing` flag stuck.
    return {
      ok: false,
      failure: publishErrorFromThrown(e),
      warnings: allWarnings,
      log: [],
    };
  } finally {
    worker.terminate();
  }
}
